// Mutations for PRO Deployment Management. All mutations:
//   1. Call the API adapter.
//   2. On success: invalidate the page queries so the page rerenders with the
//      server's authoritative state — including the 변경됨 status, which the
//      server computes (FEATURE_025); nothing is marked in the browser.

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { managementGuidelineQueryKeys } from '@/features/management-guidelines/hooks/use-guidelines';
import { proItemDesignQueryKeys } from '@/features/pro-item-design/hooks/use-pro-tree';
import { questionnaireDesignQueryKeys } from '@/features/questionnaire-design/hooks/use-item-questions';

import { proDeploymentApi } from '../api/pro-deployment.adapter';
import type {
  DeployInput,
  UpdateModuleInput,
  UpdatePageConfigInput,
} from '../types/pro-deployment.types';
import { proDeploymentQueryKeys } from './use-deployment-current-config';

export function useUpdatePageConfigMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdatePageConfigInput) => proDeploymentApi.updatePageConfig(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: proDeploymentQueryKeys.all });
    },
  });
}

export function useUpdateModuleMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mid, input }: { mid: string; input: UpdateModuleInput }) =>
      proDeploymentApi.updateModule(mid, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: proDeploymentQueryKeys.all });
    },
  });
}

// PRD 06 §3.4 / PRD 21 §10 — atomic publish of one disease code.
export function useDeployMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DeployInput) => proDeploymentApi.deploy(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: proDeploymentQueryKeys.all });
      // FEATURE_025 — the three design screens show a 변경됨 badge computed against the deployed copy, which
      // this deploy just replaced, so they re-read too.
      queryClient.invalidateQueries({ queryKey: proItemDesignQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: questionnaireDesignQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: managementGuidelineQueryKeys.all });
    },
  });
}
